import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Users, Camera } from 'lucide-react';
import { RaceGroup, Participant, Race } from '@/types';
import { supabase } from '@/integrations/supabase/client';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { getParticipantRanges, getCachedRanges } from '@/lib/participantRangesCache';

// Re-export for backward compat with GroupPhotoGallery
export const groupRangesCache = {
  get(key: string) {
    const [raceId, eventSwissId] = key.split(':');
    return getCachedRanges(raceId, eventSwissId);
  }
};

interface GroupedRaceViewProps {
  race: Race;
  eventName: string;
  eventSwissId: string;
  onBack: () => void;
  onCartClick?: () => void;
}

export function GroupedRaceView({ race, eventName, eventSwissId, onBack, onCartClick }: GroupedRaceViewProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [groups, setGroups] = useState<RaceGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [groupThumbnails, setGroupThumbnails] = useState<Record<string, string[]>>({});
  const [allParticipants, setAllParticipants] = useState<Participant[]>([]);

  useEffect(() => {
    fetchGroups();
  }, [race.id]);

  const fetchGroups = async () => {
    try {
      const { data: groupsData, error: groupsError } = await supabase
        .from('race_groups')
        .select('id, race_id, name, photo_to, group_order, participant_ids, created_at, updated_at')
        .eq('race_id', race.id)
        .order('group_order');

      if (groupsError) throw groupsError;

      const { data: participantsData, error: participantsError } = await supabase
        .from('participants')
        .select('id, race_id, swiss_equestrian_rider_id, swiss_equestrian_horse_id, rider_name, horse_name, starting_number, race_order, photo_to, total_photos, group_id, created_at, updated_at')
        .eq('race_id', race.id);

      if (participantsError) throw participantsError;

      // Attach participants to groups
      const groupsWithParticipants = groupsData.map(group => ({
        ...group,
        participants: participantsData.filter(p => p.group_id === group.id)
      }));

      setGroups(groupsWithParticipants);
      setAllParticipants(participantsData);
      
      // Load thumbnails for groups
      if (groupsWithParticipants.length > 0) {
        loadGroupThumbnails(groupsWithParticipants);
      }
    } catch (error) {
      console.error('Error fetching groups:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadGroupThumbnails = async (groups: RaceGroup[]) => {
    try {
      const result = await getParticipantRanges(race.id, eventSwissId, 6);

      const thumbnailData: Record<string, string[]> = {};
      result.participantRanges.forEach((groupData: any) => {
        if (groupData.photo_range?.urls) {
          thumbnailData[groupData.id] = groupData.photo_range.urls.slice(0, 6);
        }
      });

      setGroups(prev => prev.map(group => {
        const groupData = result.participantRanges.find((g: any) => g.id === group.id);
        return groupData ? { 
          ...group, 
          computed_photo_count: groupData.photo_range?.file_ids?.length || 0 
        } : group;
      }));

      setGroupThumbnails(thumbnailData);
    } catch (error) {
      console.error('Error loading group thumbnails:', error);
    }
  };

  const handleGroupClick = (group: RaceGroup) => {
    // Navigate to the group gallery page
    navigate(`/${eventSwissId}/${race.swiss_equestrian_race_id}/group/${group.id}`);
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'B100': return 'bg-red-100 text-red-800 border-red-200';
      case 'B/R 90': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'L80': return 'bg-green-100 text-green-800 border-green-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <Button onClick={onBack} variant="ghost" className="mb-6">
          <ArrowLeft className="w-4 h-4 mr-2" />
          {t('raceDetail.backToEvent')} {eventName}
        </Button>
        
        <div className="animate-pulse space-y-6">
          <div className="h-32 bg-muted rounded-lg"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-48 bg-muted rounded-lg"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Back Button */}
      <Button 
        onClick={onBack} 
        variant="ghost" 
        className="mb-6 hover:bg-primary/10 text-primary"
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        {t('raceDetail.backToEvent')} {eventName}
      </Button>

      {/* Race Header */}
      <Card className="mb-8 shadow-card">
        <CardHeader>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Badge className={getCategoryColor(race.category)}>
                {race.category}
              </Badge>
              <Badge variant="outline" className="bg-primary/10 text-primary">
                GROUPED MODE
              </Badge>
            </div>
            <Badge variant="outline">
              {groups.length} {groups.length === 1 ? 'Gruppo' : 'Gruppi'}
            </Badge>
          </div>
          <CardTitle className="text-2xl md:text-3xl font-bold text-primary">
            {race.name}
          </CardTitle>
          <div className="flex flex-wrap gap-4 text-sm text-muted-foreground mt-2">
            <span>{t('raceDetail.date')}: {new Date(race.race_date).toLocaleDateString()}</span>
            {race.race_time && <span>{t('raceDetail.time')}: {race.race_time}</span>}
          </div>
        </CardHeader>
      </Card>

      {/* Groups Grid */}
      <div className="mb-6">
        <h2 className="text-2xl font-semibold mb-4 flex items-center">
          <Users className="w-6 h-6 mr-2 text-primary" />
          Gruppi Partecipanti
        </h2>
        <p className="text-muted-foreground mb-4">
          Clicca su un gruppo per visualizzare le foto dei partecipanti.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {groups.map((group) => {
          const hasPhotos = groupThumbnails[group.id]?.length > 0;
          
          return (
            <Card 
              key={group.id}
              className="group hover:shadow-lg transition-all duration-300 cursor-pointer transform hover:scale-[1.02] overflow-hidden"
              onClick={() => handleGroupClick(group)}
            >
              <div className="relative h-40 bg-gradient-to-br from-primary/10 to-primary/5 group-hover:from-primary/20 group-hover:to-primary/10 transition-all duration-300">
                {/* Group thumbnail preview */}
                <div className="absolute inset-2 overflow-hidden rounded-sm">
                  <div className="flex gap-1 h-full">
                    {groupThumbnails[group.id]?.map((thumbnailUrl, i) => (
                      <div 
                        key={`img-${i}`}
                        className="flex-shrink-0 bg-primary/20 rounded-sm overflow-hidden aspect-square"
                        style={{ width: `${100/6}%` }}
                      >
                        <img 
                          src={thumbnailUrl} 
                          alt={`Preview ${i + 1}`}
                          className="w-full h-full object-cover transition-opacity duration-300"
                          loading="lazy"
                        />
                      </div>
                    )) || [...Array(6)].map((_, i) => (
                      <div 
                        key={`placeholder-${i}`}
                        className="flex-shrink-0 bg-primary/20 rounded-sm animate-pulse"
                        style={{ width: `${100/6}%` }}
                      />
                    ))}
                  </div>
                </div>
                
                <div className="absolute top-2 right-2">
                  <Badge className="bg-black/80 text-white border-0 text-xs shadow-lg">
                    {(group as any).computed_photo_count || group.photo_to || 0} foto
                  </Badge>
                </div>
                
                <div className="absolute top-2 left-2">
                  <Badge variant="outline" className="bg-white/95 text-xs shadow-lg">
                    <Users className="w-3 h-3 mr-1" />
                    {group.participants?.length || 0}
                  </Badge>
                </div>
                
                {/* Group name overlay */}
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 via-black/60 to-transparent p-3">
                  <div className="text-white text-lg font-bold truncate drop-shadow-lg">
                    {group.name}
                  </div>
                </div>
              </div>
              
              <CardContent className="p-4">
                {/* Participants list */}
                <div className="relative mb-4">
                  <div className="space-y-2 max-h-32 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent pb-12">
                    {group.participants?.map((participant) => (
                      <div key={participant.id} className="flex items-center text-sm">
                        <span className="font-medium">{participant.rider_name}</span>
                        {participant.horse_name && (
                          <span className="text-muted-foreground ml-2">- {participant.horse_name}</span>
                        )}
                        {participant.starting_number && (
                          <Badge variant="outline" className="ml-auto text-xs">
                            #{participant.starting_number}
                          </Badge>
                        )}
                      </div>
                    ))}
                  </div>
                  {/* Fade gradient at bottom to indicate more content */}
                  {(group.participants?.length || 0) > 4 && (
                    <div className="absolute bottom-0 left-0 right-0 h-12 bg-gradient-to-t from-background via-background/80 to-transparent pointer-events-none" />
                  )}
                </div>
                
                <Button 
                  size="sm"
                  className="w-full bg-primary hover:bg-primary/90"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleGroupClick(group);
                  }}
                >
                  <Camera className="w-4 h-4 mr-2" />
                  Visualizza Galleria
                </Button>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {groups.length === 0 && !loading && (
        <div className="text-center py-12">
          <Users className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
          <h3 className="text-xl font-semibold mb-2">Nessun gruppo trovato</h3>
          <p className="text-muted-foreground">
            Questa gara non ha ancora gruppi configurati.
          </p>
        </div>
      )}

    </div>
  );
}